/**
 * What the model must return when reading a photo of a distribution board.
 * Kept deliberately close to what is printed on the devices: the model reads, it does not design.
 */
import { z } from 'zod';

export const deviceTypes = [
  'main-switch',
  'RCD',
  'MCB',
  'RCBO',
  'fuse',
  'contactor',
  'timer',
  'meter',
  'other',
] as const;

export const boardDeviceSchema = z.object({
  row: z.number().int().min(1).describe('DIN rail number, 1 = top rail'),
  index: z.number().int().min(1).describe('Position on the rail, 1 = leftmost device'),
  deviceType: z.enum(deviceTypes),
  ratingA: z
    .number()
    .positive()
    .nullable()
    .describe('Rated current in amperes, e.g. 16 for "C16"; null if unreadable'),
  curve: z.enum(['B', 'C', 'D']).nullable().describe('Tripping curve letter, if printed'),
  poles: z.number().int().min(1).max(4).nullable(),
  sensitivityMa: z.number().positive().nullable().describe('RCD/RCBO sensitivity in mA (30, 300...)'),
  rcdType: z.enum(['AC', 'A', 'F', 'B']).nullable().describe('RCD type marking, if visible'),
  label: z.string().nullable().describe('Handwritten or printed label for the circuit, verbatim'),
  confidence: z.number().min(0).max(1).describe('How sure you are about this device overall'),
});

export const boardExtractionSchema = z.object({
  devices: z.array(boardDeviceSchema),
  notes: z
    .string()
    .describe('Anything unreadable, ambiguous or suspicious (e.g. missing labels, unusual wiring)'),
});

export type BoardDevice = z.infer<typeof boardDeviceSchema>;
export type BoardExtraction = z.infer<typeof boardExtractionSchema>;
